import React from 'react'
import styled from 'styled-components'
import { graphql, compose } from 'react-apollo'
import PropTypes from 'prop-types'

import ProductSwitchReasons from './ProductSwitchReasons'
import LoadingButton from '../../../common/LoadingButton'
import ValidationError from '../../../common/ValidationError'
import WithLoan from '../../../shared/WithLoan'
import AppDetailsQuery from '../../queries/AppDetailsQuery'
import UpdateLoan from '../../../common/queries/UpdateLoan'

const Container = styled.div`
  border: 1px solid #b9b9b9;
  box-sizing: border-box;
  background: #f1f1f1;
`
const Header = styled.div`
  background-image: linear-gradient(to bottom, #ffffff, #d0d0d0);
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  width: 100%;
  border-bottom: 1px dashed #dddddd;
  height: 30px;
  padding: 0px 0px 0px 5px;
  align-items: center;
  box-sizing: border-box;
`
const EditContainer = styled.div`
  padding: 20px;
`
const Cancel = styled.div`
  justify-self: end;
  margin-right: 25px;
`
const Select = styled.select`

`
const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: 20px;
  margin-bottom: 20px;
`
const EditLink = styled.a`
  cursor: pointer;
`

export class EditProduct extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      selectedProduct: props.product,
      reasons: [],
      note: '',
      isLoading: false
    }
  }
  static propTypes = {
    loan: PropTypes.shape({
      amount: PropTypes.number,
      product: PropTypes.object.isRequired,
      hardCreditPulled: PropTypes.bool
    }),
    onCancel: PropTypes.func.isRequired,
    product: PropTypes.object.isRequired,
    products: PropTypes.arrayOf(PropTypes.object),
    productSwitchReasons: PropTypes.arrayOf(PropTypes.object),
    updateLoan: PropTypes.func.isRequired
  }
  onReasonCheck = e => {
    const { reasons } = this.state
    const { value, checked } = e.target
    let newReasonsList
    if (checked) {
      newReasonsList = reasons.concat(value)
    } else {
      // Make a copy and remove the value that has been unchecked
      newReasonsList = reasons.slice()
      newReasonsList.splice(reasons.indexOf(value), 1)
    }
    this.setState({
      reasons: newReasonsList
    })
  }
  onNoteChange = e => {
    this.setState({
      note: e.target.value
    })
  }
  onProductChange = e => {
    const { products } = this.props
    this.setState({
      selectedProduct: products.find(product => product.name === e.target.value)
    })
  }
  onSubmit = async () => {
    const { onCancel } = this.props
    this.setState({ isLoading: true })
    await this._updateLoanProduct()
    this.setState({ isLoading: false })
    onCancel()
    location.reload()
  }
  _updateLoanProduct = async () => {
    const { updateLoan, loan } = this.props
    const { selectedProduct, note, reasons } = this.state
    const { rid } = loan
    return updateLoan({
      variables: {
        input: {
          rid,
          productId: selectedProduct.rid,
          reasonKeys: reasons,
          note
        },
        loanId: rid
      },
      update: (store, { data: { updateLoan: { loan } } }) => {
        const productsCategory = ['working_capital', 'cre', 'paycheck_protection']
        const data = store.readQuery({ query: AppDetailsQuery, variables: { loanId: rid, productsCategory } })
        data.loan.product = loan.product
        store.writeQuery({ query: AppDetailsQuery, variables: { loanId: rid, productsCategory }, data })
      }
    })
  }
  isNoteValid = (reasons, note) => {
    return this.isNoteRequired(reasons) ? note.length > 0 : true
  }
  isNoteRequired = reasons => {
    return reasons.includes('other')
  }
  isProductValidForLoanAmount = ({ product, amount }) => {
    const { manualMin, manualMax } = product
    return amount <= manualMax && amount >= manualMin
  }
  isProductSame = ({ product, selectedProduct }) => {
    return product.name === selectedProduct.name
  }
  isLenderSame = (current_lender, new_lender) => {
    return current_lender === new_lender
  }
  isCreditPullValidForProductChange = (hardCreditPulled, customerService, isLenderSame) => {
    return hardCreditPulled ? customerService && isLenderSame : true
  }
  isValid = () => {
    const { loan, customerService } = this.props
    const { amount, product, hardCreditPulled } = loan
    const { reasons, note, selectedProduct } = this.state
    const { lenderKey } = product
    const reasonSelected = reasons.length > 0
    const isNoteValid = this.isNoteValid(reasons, note)
    const isProductValid = this.isProductValidForLoanAmount({ product: selectedProduct, amount }) && !this.isProductSame({ product, selectedProduct })
    const isLenderSame = this.isLenderSame(lenderKey, selectedProduct.lenderKey)
    const isCreditPullValidForProductChange = this.isCreditPullValidForProductChange(hardCreditPulled, customerService, isLenderSame)
    return isCreditPullValidForProductChange && isNoteValid && reasonSelected && isProductValid
  }
  renderLoanAmountError = (selectedProduct, amount) => {
    if (amount < selectedProduct.manualMin) {
      return 'Current loan amount is below selected product\'s minimum loan amount'
    } else if (amount > selectedProduct.manualMax) {
      return 'Current loan amount exceeds selected product\'s max loan amount'
    }
  }
  renderValidationErrors = () => {
    const { product, loan, customerService } = this.props
    const { selectedProduct, reasons } = this.state
    const { amount, hardCreditPulled } = loan
    const { lenderKey } = product
    const isLenderSame = this.isLenderSame(lenderKey, selectedProduct.lenderKey)
    if (!this.isCreditPullValidForProductChange(hardCreditPulled, customerService, isLenderSame)) {
      return 'The loan has a hard pull'
    } else if (this.isProductSame({ product, selectedProduct }) && reasons.length > 0) {
      return 'Your selection must be different than the assigned loan product.'
    } else if (!this.isProductValidForLoanAmount({ product: selectedProduct, amount })) {
      return this.renderLoanAmountError(selectedProduct, amount)
    } else {
      return <span>&nbsp;</span> // Placeholder to prevent content jumping
    }
  }
  render() {
    // This has to be a full-width <td></td> because it's a child of a <tr></tr>
    // When App Details is finally resdesigned/axed, this should be a div
    const { product, onCancel, products, productSwitchReasons } = this.props
    const { selectedProduct, isLoading, note } = this.state
    const { name } = product
    return (
      <td colSpan="3">
        <Container>
          <Header>
            <div>
              Loan Product
            </div>
            <div>
              {name}
            </div>
            <Cancel>
              <EditLink onClick={onCancel}>Cancel</EditLink>
            </Cancel>
          </Header>
          <EditContainer>
            <Select value={selectedProduct.name} onChange={this.onProductChange}>
              {products.map(product => <option value={product.name} key={product.name}>{product.name}</option>)}
            </Select>
            <ValidationError visible>
              {this.renderValidationErrors()}
            </ValidationError>
            <ProductSwitchReasons
              productSwitchReasons={productSwitchReasons}
              onReasonCheck={this.onReasonCheck}
              onNoteChange={this.onNoteChange}
              showNoteError={!this.isNoteValid(this.state.reasons, note)}
            />
          </EditContainer>
          <ButtonContainer>
            <LoadingButton disabled={!this.isValid()} onClick={this.onSubmit} loading={isLoading}>
              Update Loan Product
            </LoadingButton>
          </ButtonContainer>
        </Container>
      </td>
    )
  }
}

export default compose(
  graphql(UpdateLoan, {
    name: 'updateLoan'
  }),
  WithLoan
)(EditProduct)
